import { useEffect, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getBusLocations, getBusLocationsWithQuery, searchBusLocations } from '../../../api/endpoints/bus';
import type { BusLocationOption, BusLocationRecord } from '../../../api/types/bus';

/** Origin/destination autocomplete. Primary source is `BusLocation/GetWithQuery` (confirmed
 *  to return real results in every environment tried). When that yields nothing or errors,
 *  the picker falls back to a client-side filter over the full `BusLocation/Get` list, plus
 *  the provider-backed `Bus/Location/Search` as an alternative source. Debounces input by
 *  300ms and requires 3+ characters, matching the "en az 3 harf" rule the API enforces. */
export function BusLocationAutocomplete({
  label,
  value,
  onChange,
}: {
  label: string;
  value: BusLocationOption | null;
  onChange: (value: BusLocationOption | null) => void;
}) {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<BusLocationOption[]>([]);
  const [loading, setLoading] = useState(false);

  // Full list — fetched once, lazily, only used as the fallback filter source.
  const { data: fullList } = useQuery({
    queryKey: ['bus', 'locationsFull'],
    queryFn: () => getBusLocations(),
    staleTime: 60 * 60_000,
  });

  useEffect(() => {
    const term = inputValue.trim();
    if (term.length < 3) {
      setOptions([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(() => {
      getBusLocationsWithQuery(term)
        .then((result) => (result.length > 0 ? result : Promise.reject(new Error('empty'))))
        .catch(async () => {
          // Fallback: client-filter the full list + provider search, merged & de-duped by slug.
          const lower = term.toLocaleLowerCase('tr');
          const fromFull = (fullList ?? []).filter((l: BusLocationRecord) =>
            (l.name ?? '').toLocaleLowerCase('tr').includes(lower)
          );
          const fromProvider = await searchBusLocations(term)
            .then((r) => r.locations ?? [])
            .catch(() => []);
          const bySlug = new Map<string, BusLocationOption>();
          for (const l of [...fromFull, ...fromProvider] as BusLocationOption[]) {
            bySlug.set(l.slug ?? l.name ?? '', { name: l.name, slug: l.slug, cityCode: l.cityCode });
          }
          return Array.from(bySlug.values());
        })
        .then((result) => {
          if (!cancelled) setOptions(result);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [inputValue, fullList]);

  return (
    <Autocomplete
      value={value}
      onChange={(_, newValue) => onChange(newValue)}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
      options={options}
      loading={loading}
      getOptionLabel={(option) => option.name ?? ''}
      isOptionEqualToValue={(option, val) => option.slug === val.slug}
      noOptionsText={inputValue.trim().length < 3 ? 'En az 3 harf girin' : 'Sonuç bulunamadı'}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.slotProps.input.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
}
